import { FieldWrapper } from '@progress/kendo-react-form';
import { ColorPicker, ColorPickerChangeEvent, InputPrefix, InputSuffix, NumericTextBox, NumericTextBoxChangeEvent, Switch } from '@progress/kendo-react-inputs';
import { useEffect, useState } from 'react';
import ThresholdChart from '../ThresholdChart';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import { Card, CardBody } from '@progress/kendo-react-layout';

export default function RightAmountComponent(props)
{
  const localizationService = useLocalization();
 
    const initialThresholds = props.value?.thresholds 
        ? [...props.value.thresholds].sort((a, b) => b.id - a.id)  // Sort by ID in descending order
        : [];

    const [cardData, setCardData] = useState<any>(initialThresholds);
    const [cardDataAutoOff, setCardDataAutoOff] = useState<any>(initialThresholds);
    const [volumeRequirement, setVolumeRequirement] = useState<any>(props.value ? props.value.volumeRequirement : 2.0);
    const [autoThresholds, setAutoThresholds] = useState<boolean>(props.value && props.value.autoThresholds !== undefined ? props.value.autoThresholds : true);
    const [autoThresholdPercentage, setAutoThresholdPercentage] = useState<number>(props.value ? props.value.autoThresholdPercentage : 20.0);

    const complianceThresholdIdInitial = () =>
      props.value && props.value.complianceThresholdId !== undefined && cardData.find(x => x.id === props.value.complianceThresholdId) !== undefined? {value: cardData.find(x => x.id === props.value.complianceThresholdId)}: {
        value: cardData[0]
      }
    const [complianceThresholdId] = useState<any>(complianceThresholdIdInitial());
 
    const suffix = () => (<InputSuffix>cc&nbsp;&nbsp;</InputSuffix>);
    const prefix = () => (<InputPrefix>&nbsp;&nbsp;{'±'}</InputPrefix>);

    const paletteSettings = {
      palette: [
        "rgba(255, 80, 52, 1)",
        "rgba(26, 179, 148, 1)",
        "rgba(127, 127, 127, 1)",
        "rgba(136, 0, 21, 1)",
        "rgba(255, 127, 39, 1)",
        "rgba(255, 242, 0, 1)",
        "rgba(0, 162, 232, 1)",
        "rgba(63, 72, 204, 1)",
        "rgba(163, 73, 164, 1)",
        "rgba(0, 0, 0, 1)",
        "rgba(255, 174, 201, 1)",
        "rgba(181, 230, 29, 1)",
        "rgba(217, 217, 217, 1)",
        "rgba(185, 122, 87, 1)",
        "rgba(255, 201, 14, 1)",
        "rgba(239, 228, 176, 1)",
        "rgba(153, 217, 234, 1)",
        "rgba(112, 146, 190, 1)",
        "rgba(200, 191, 231, 1)",
        "rgba(255, 255, 255, 1)"
      ],
      columns: 10,
      tileSize: 25.5,
    };
 
    useEffect(() => {
        const rightAmount = {volumeRequirement: volumeRequirement, autoThresholds: autoThresholds, autoThresholdPercentage: autoThresholdPercentage, thresholds: cardData, complianceThresholdId: complianceThresholdId.value.id};
        props.onChange({ value: rightAmount});
    },[volumeRequirement, autoThresholds, autoThresholdPercentage, cardData, complianceThresholdId]);
 
    const onVolumeChange = (item: any) => {
        // Ensure volume requirement is not less than 0.1 when changed from chart
        const safeValue = Math.max(item || 0.1, 0.1);
        setVolumeRequirement(safeValue);
    };
 
    const VolumeRequirementInput = () => {
        return (
            <NumericTextBox
                key={"volumeRequirementInput"}
                label={""}
                width={151}
                value={volumeRequirement}
                onChange={(event:NumericTextBoxChangeEvent) => {
                  setTimeout(() => {
                    // Ensure volume requirement is not less than 0.1
                    const safeValue = Math.max(event.value || 0.1, 0.1);
                    setVolumeRequirement(safeValue);
                  }, 350)}}
                format={"n2"}
                min={0.1}
                step={0.1}
                suffix={suffix}
            />
        );
    };
 
    useEffect(() => {
      const newData = [...(autoThresholds ? cardData : cardDataAutoOff)];

      if (autoThresholds) {
        // Auto ON mode - always update all thresholds
        newData[0].value = volumeRequirement * (1 + autoThresholdPercentage);
        newData[1].value = volumeRequirement * (1 - autoThresholdPercentage);
        newData[2].value = 0;
      } else {
        // Auto OFF mode - check each index independently
        let shouldUpdate = false;

        // Check and update each index based on its own condition
        if (volumeRequirement > newData[0].value) {
          newData[0].value = volumeRequirement * (1 + autoThresholdPercentage);
          shouldUpdate = true;
        }
        
        // TODO: Uncomment this when the issue is fixed
        // if (volumeRequirement < newData[1].value) {
        //   newData[1].value = volumeRequirement * (1 - autoThresholdPercentage);
        //   shouldUpdate = true;
        // }

        newData[2].value = 0;

        if (!shouldUpdate) return; // Avoid unnecessary state updates
      }

      setCardData(newData);
      setCardDataAutoOff(newData);
    }, [autoThresholds, autoThresholdPercentage, volumeRequirement]);
    
    const onThresholdChange = (event:any) => {
        setAutoThresholds(event.target.value);
    };
 
    const AutoThresholdInput = () => {
 
        return (
          <Switch
            size={"small"}
            trackRounded={"medium"}
            thumbRounded={"medium"}
            checked={autoThresholds}
            onChange={onThresholdChange}
          />
        );
    };
 
    const PercentageInput = () => {
 
        return (
            <NumericTextBox
                width={100}
                format="p"
                value={autoThresholdPercentage}
                min={0}
                max={1}
                step={0.01}
                onChange={(event) => setAutoThresholdPercentage(event.target.value ? event.target.value : 0.2)}
                disabled={!autoThresholds}
                prefix={prefix}
            />
        );
    };
 
    const updateCard = (props: any[]) => {
        setCardData([...props]); // Guarantees a new reference (helps React detect changes).
      };
      
      const ThresholdListInput = () => {
        // Sort cardData by ID in descending order only for display
        const sortedCardData = [...cardData].sort((a, b) => b.id - a.id);

        return (
            <>
                {sortedCardData.map((item, index) => {
                    // Calculate max and min for each item
                    let max, min = 0;
                    if (index !== 0) {
                        max = sortedCardData[index - 1].value - 0.1;
                    }
                    if (index !== sortedCardData.length - 1) {
                        min = sortedCardData[index + 1].value + 0.1;
                    }

                    return (
                        <div style={{display: 'flex', alignItems:'flex-end', marginBottom:'5px'}} key={item.id}>
                            <div>
                                <div className={"k-card-list requirements-cards " } >
                                    <Card style={{  display:'flex'}}>
                                        <CardBody style={{display:"flex", alignItems:"center", justifyContent:'space-between', height:'45px'}}>
                                            <div style={{display:'flex',  alignItems:"center"}}>
                                                <div className="col-sm-12 col-md-3 " style={{ marginRight: '10px' }}>
                                                    <ColorPicker
                                                        view={"combo"}
                                                        value={`${item.colour}`}
                                                        onChange={(event: ColorPickerChangeEvent) => {
                                                            setCardData(prevCardData =>
                                                                prevCardData.map((prevItem, i) =>
                                                                    i === index ? { ...prevItem, colour: event.value } : prevItem
                                                                )
                                                            );
                                                        }}
                                                        paletteSettings={paletteSettings}      
                                                        defaultValue={"#FF5034"}
                                                    />
                                                </div>
                                          
                                                <input
                                                    style={{width:"290px", border:'none'}}
                                                    defaultValue={item.name}
                                                    maxLength={50}
                                                    placeholder={'Enter a threshold label...'}
                                                    readOnly={true}
                                                    unselectable={"on"}
                                                />
                                            </div>
                                            <div style={{display:'flex',  alignItems:"center"}}>
                                                <div>
                                                    <NumericTextBox
                                                        width={160}
                                                        prefix={prefix}
                                                        suffix={suffix}
                                                        placeholder="please enter value"
                                                        defaultValue={item.value}
                                                        max={max}
                                                        min={min}
                                                        value={index === 2 ? 0 : item.value}
                                                        onChange={(event: NumericTextBoxChangeEvent) => {
                                                            setTimeout(() => {
                                                                setCardData(prevCardData =>
                                                                    prevCardData.map((prevItem, i) => 
                                                                        i === index ? { ...prevItem, value: i === 2 ? 0 : event.value } : prevItem
                                                                    )
                                                                );
                                                            }, 350);
                                                        }}
                                                        format={"n2"}
                                                        step={0.1}
                                                        disabled={autoThresholds || index === 2}
                                                    />
                                                </div>  
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };
 
    return (
        <FieldWrapper>
            <div>
                <div style={{display:'flex', alignItems:'baseline', gap:'5px', margin:'20px 0'}}>
                    <div style={{display:'flex', alignItems:'baseline', gap:'5px'}}>
                        <label className=''>{localizationService.toLanguageString('custom.volumeRequirement', enMessages.custom.volumeRequirement)}:</label>
                        {VolumeRequirementInput()}
                    </div>
 
                    <div style={{display:'flex', alignItems:'center', gap:'5px', margin:'0 10px'}}>
                        <label className=''>{localizationService.toLanguageString('custom.autoThreshold', enMessages.custom.autoThreshold)}:</label>
                        <AutoThresholdInput />
                        {PercentageInput()}
                    </div>
                </div>
            </div>
 
            <div className='' style={{display:"grid", gridTemplateColumns:'2fr 1fr', gap:'10px'}}>
                <div style={{textAlign:'center'}}>
                    <div style={{ maxHeight:'400px'}}>
                    {ThresholdListInput()}
                    </div>
                </div>
                <ThresholdChart threshold={cardData} volume={volumeRequirement} updateChart={updateCard} updateVolume={onVolumeChange} checked={autoThresholds}/>
            </div>

        </FieldWrapper>
    );
}